import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../../../contexts/AuthContext'
import ShopProductCard from '../../../components/shop/ShopProductCard'
import Pagination from '../../../components/common/Pagination'
import { handleGetRequest } from '../../../utils/api-handler.util'
import { QueryKeys } from '../../../utils/queryKeyGenerator'
import useProductCategories from '../../../hooks/useProductCategories'
import PageTitle from '../../../components/common/PageTitle'

const MyProductsPage = () => {
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  const { categories } = useProductCategories()
  const [currentPage, setCurrentPage] = useState(1)

  const { data: products, isLoading } = useQuery({
    queryKey: QueryKeys.listShopProducts(currentUser?.userType, currentPage),
    queryFn: () => handleGetRequest(`${currentUser.userType}s/my-products?page=${currentPage}`),
    enabled: !!currentUser?.userType,
  })

  const productList = products?.data || []
  const paginationMeta = products?.meta

  const handleAddProduct = () => {
    navigate(`/${currentUser?.userType}/add-product`)
  }

  const handleProductClick = (productSlug) => {
    navigate(`/${currentUser?.userType}/products/${productSlug}`)
  }

  if (isLoading) {
    return (
      <div className="main-container min-h-screen">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading products...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="main-container">
      <PageTitle className="w-full flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 lg:gap-6">
        <span className="text-xl lg:text-2xl"> My Products</span>
        <button
          onClick={handleAddProduct}
          className="bg-secondary-600 text-white px-6 py-2 lg:px-8 lg:py-3 rounded-lg text-sm lg:text-base transition-colors font-medium hover:opacity-90"
        >
          Add Product
        </button>
      </PageTitle>

      <div className="mb-6 lg:mb-8">
        <span className="text-gray-600 text-sm lg:text-base">
          Total Products: {paginationMeta?.total || productList?.length || 0}
        </span>
      </div>

      {productList.length === 0 ? (
        <div className="text-center py-12 lg:py-16">
          <div className="text-gray-500 text-lg lg:text-xl mb-4">No products found</div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
            {productList.map((product) => (
              <ShopProductCard
                key={product.id}
                product={product}
                categories={categories}
                onClick={() => handleProductClick(product.productSlug)}
              />
            ))}
          </div>
          {paginationMeta && (
            <div className="mt-8 lg:mt-12">
              <Pagination paginationMeta={paginationMeta} onPageChange={setCurrentPage} />
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default MyProductsPage
